<template>
  <SummarizationContainerCommon
    :loading="loading"
    :is-transcription-result-empty="isTranscriptionResultEmpty"
  >
    <SummarizationHeaderCommon
      :created-at="followUpNecessityJudgmentResult?.createdAt || dealAcquiredJudgmentResult?.createdAt"
      :is-refetch-button-visible="canRefresh"
      :can-recreate="canRecreate"
      @click:recreate="handleRecreateClick"
      @click:refetch="fetchData"
    />
    <SummarizationContentCommon
      :content="voicemailJudgmentResultContent"
    />
    <SummarizationInfoMessageBodyCommon
      v-if="!!voicemailJudgmentInfoMessage"
      class="processing"
      :message="voicemailJudgmentInfoMessage"
    />
    <SummarizationErrorBodyCommon
      v-if="voicemailJudgmentResult?.creationStatus === 'failed'"
      class="error"
      :resource-name="i18n.t('transcription.voicemailJudgment')"
    />
    <SummarizationContentCommon
      :content="dealAcquiredJudgmentResultContent"
    />
    <SummarizationInfoMessageBodyCommon
      v-if="!!dealAcquiredJudgmentInfoMessage"
      class="processing"
      :message="dealAcquiredJudgmentInfoMessage"
    />
    <SummarizationErrorBodyCommon
      v-if="dealAcquiredJudgmentResult?.creationStatus === 'failed'"
      class="error"
      :resource-name="i18n.t('transcription.dealAcquiredJudgment')"
    />
    <SummarizationContentCommon
      :content="followUpNecessityJudgmentResultContent"
    />
    <SummarizationInfoMessageBodyCommon
      v-if="!!followUpNecessityJudgmentInfoMessage"
      class="processing"
      :message="followUpNecessityJudgmentInfoMessage"
    />
    <SummarizationErrorBodyCommon
      v-if="followUpNecessityJudgmentResult?.creationStatus === 'failed'"
      class="error"
      :resource-name="i18n.t('transcription.followUpNecessityJudgment')"
    />
  </SummarizationContainerCommon>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { DealAcquiredJudgmentResult, FollowUpNecessityJudgmentResult, VoicemailJudgmentResult } from '@/api/openapi';
import { DealAcquiredJudgmentResultApiService } from '@/api/user/resources/deal_acquired_judgment_result';
import { FollowUpNecessityJudgmentResultApiService } from '@/api/user/resources/follow_up_necessity_judgment_result';
import { VoicemailJudgmentResultApiService } from '@/api/user/resources/voicemail_judgment_result';
import { useAlert } from '@/composable/message-dialog';
import { useAutoRetry } from '@/composable/use-auto-retry';
import { useWait } from '@/composable/vue-wait';
import SummarizationContainerCommon from './SummarizationContainerCommon.vue';
import SummarizationContentCommon from './SummarizationContentCommon.vue';
import SummarizationErrorBodyCommon from './SummarizationErrorBodyCommon.vue';
import SummarizationHeaderCommon from './SummarizationHeaderCommon.vue';
import SummarizationInfoMessageBodyCommon from './SummarizationInfoMessageBodyCommon.vue';

type TProps = {
  callResultId: number;
  isTranscriptionResultEmpty: boolean;
};
const props = defineProps<TProps>();

const voicemailJudgmentResult = ref<VoicemailJudgmentResult | null>(null);
const dealAcquiredJudgmentResult = ref<DealAcquiredJudgmentResult | null>(null);
const followUpNecessityJudgmentResult = ref<FollowUpNecessityJudgmentResult | null>(null);

const { doActionWithWait, wait } = useWait();
const i18n = useI18n();
const { alert } = useAlert();

const voicemailJudgmentResultContent = computed(() => {
  return `## ${i18n.t('transcription.voicemailJudgmentResult.title')}
${voicemailJudgmentResult.value?.markdown ?? ''}
`;
});

const dealAcquiredJudgmentResultContent = computed(() => {
  return `## ${i18n.t('transcription.dealAcquiredJudgmentResult.title')}
${dealAcquiredJudgmentResult.value?.markdown ?? ''}
`;
});

const followUpNecessityJudgmentResultContent = computed(() => {
  const title = `## ${i18n.t('transcription.followUpNecessityJudgmentResult.title')}`;
  const resultMarkdown = followUpNecessityJudgmentResult.value?.resultMarkdown;
  if (!resultMarkdown) return title;

  const hearingItemsMarkdown = followUpNecessityJudgmentResult.value?.hearingItemExtractionResultMarkdown;
  if (!hearingItemsMarkdown) return `${title}
${resultMarkdown}
`;

  const hearingItemsTitle = `### ${i18n.t('transcription.followUpNecessityJudgmentResult.hearingItems.title')}`;

  return `${title}
${resultMarkdown}
${hearingItemsTitle}
${hearingItemsMarkdown}
`;
});

const loading = computed(() => {
  return wait.is(['fetchFollowUpNecessityJudgmentResultWait', 'postJudgeFollowUpNecessityWait']);
});

const canRecreate = computed(() => {
  return ['failed', 'not_started', 'invalid_prompt_setting'].includes(followUpNecessityJudgmentResult.value?.creationStatus ?? '');
});

const canRefresh = computed(() => {
  return !!canRecreate.value
  || ['processing'].includes(voicemailJudgmentResult.value?.creationStatus ?? '')
  || ['processing'].includes(dealAcquiredJudgmentResult.value?.creationStatus ?? '')
  || ['processing'].includes(followUpNecessityJudgmentResult.value?.creationStatus ?? '');
});

const voicemailJudgmentInfoMessage = computed(() => {
  if (voicemailJudgmentResult.value?.creationStatus === 'not_started' && !!canRecreate.value) {
    return i18n.t('transcription.generationIsNotStarted', { resourceName: i18n.t('transcription.voicemailJudgment') });
  }
  if (voicemailJudgmentResult.value?.creationStatus === 'not_started' && !canRecreate.value) {
    return i18n.t('transcription.generationIsNotStartedWithoutRecreate', { resourceName: i18n.t('transcription.voicemailJudgment') });
  }
  if (voicemailJudgmentResult.value?.creationStatus === 'processing') {
    return i18n.t('transcription.generationIsInProgress', { resourceName: i18n.t('transcription.voicemailJudgment') });
  }
  return '';
});

const dealAcquiredJudgmentInfoMessage = computed(() => {
  if (dealAcquiredJudgmentResult.value?.creationStatus === 'not_started') {
    return i18n.t('transcription.generationIsNotStarted', { resourceName: i18n.t('transcription.dealAcquiredJudgment') });
  }
  if (dealAcquiredJudgmentResult.value?.creationStatus === 'processing') {
    return i18n.t('transcription.generationIsInProgress', { resourceName: i18n.t('transcription.dealAcquiredJudgment') });
  }
  return '';
});

const followUpNecessityJudgmentInfoMessage = computed(() => {
  if (followUpNecessityJudgmentResult.value?.creationStatus === 'not_started') {
    return i18n.t('transcription.generationIsNotStarted', { resourceName: i18n.t('transcription.followUpNecessityJudgment') });
  }
  if (followUpNecessityJudgmentResult.value?.creationStatus === 'processing') {
    return i18n.t('transcription.generationIsInProgress', { resourceName: i18n.t('transcription.followUpNecessityJudgment') });
  }
  if (followUpNecessityJudgmentResult.value?.creationStatus === 'invalid_prompt_setting') {
    return i18n.t('transcription.followUpNecessityJudgmentResult.invalidPromptSetting');
  }
  return '';
});

const fetchFollowUpNecessityJudgmentResult = async () => {
  await doActionWithWait('fetchFollowUpNecessityJudgmentResultWait', async () => {
    const api = new FollowUpNecessityJudgmentResultApiService();
    const { data: { followUpNecessityJudgmentResult: fetchedFollowUpNecessityJudgmentResult } } = await api.getFollowUpNecessityJudgmentResult({
      request: {
        callResultId: props.callResultId,
      },
      errorHandlers: {
        404: () => {
          followUpNecessityJudgmentResult.value = null;
        },
      },
    });
    followUpNecessityJudgmentResult.value = fetchedFollowUpNecessityJudgmentResult;
  });
};

const fetchDealAcquiredJudgmentResult = async () => {
  await doActionWithWait('fetchDealAcquiredJudgmentResultWait', async () => {
    const api = new DealAcquiredJudgmentResultApiService();
    const { data: { dealAcquiredJudgmentResult: fetchedDealAcquiredJudgmentResult } } = await api.getDealAcquiredJudgmentResult({
      request: { callResultId: props.callResultId },
    });
    dealAcquiredJudgmentResult.value = fetchedDealAcquiredJudgmentResult;
  });
};

const fetchVoicemailJudgmentResult = async () => {
  await doActionWithWait('fetchVoicemailJudgmentResultWait', async () => {
    const api = new VoicemailJudgmentResultApiService();
    const { data: { voicemailJudgmentResult: fetchedVoicemailJudgmentResult } } = await api.getVoicemailJudgmentResult({
      request: { callResultId: props.callResultId },
    });
    voicemailJudgmentResult.value = fetchedVoicemailJudgmentResult;
  });
};

const fetchData = async () => {
  if (props.isTranscriptionResultEmpty) return;
  await fetchFollowUpNecessityJudgmentResult();
  await fetchDealAcquiredJudgmentResult();
  await fetchVoicemailJudgmentResult();
};

const postJudgeFollowUpNecessityErrorHandler = () => {
  alert({
    title: i18n.t('transcription.failedToRecreate.title', { resourceName: i18n.t('transcription.judgmentResult') }),
    message: i18n.t('transcription.failedToRecreate.message', { resourceName: i18n.t('transcription.judgmentResult') }),
  });
};

const postJudgeFollowUpNecessity = async () => {
  await doActionWithWait('postJudgeFollowUpNecessityWait', async () => {
    const api = new FollowUpNecessityJudgmentResultApiService();
    await api.postJudgeFollowUpNecessity({
      request: { callResultId: props.callResultId },
      errorHandlers: {
        500: postJudgeFollowUpNecessityErrorHandler,
        422: postJudgeFollowUpNecessityErrorHandler,
      },
    });
    await fetchData();
  });
};

const handleRecreateClick = async () => {
  await postJudgeFollowUpNecessity();
  restartAutoRetry();
};

const checkAvailability = async (): Promise<boolean> => {
  const shouldFinishRetry = () => followUpNecessityJudgmentResult.value != null && followUpNecessityJudgmentResult.value.creationStatus !== 'processing';
  if (shouldFinishRetry()) return true;
  await fetchData();
  return shouldFinishRetry();
};

const { start: startAutoRetry, stop: stopAutoRetry } = useAutoRetry(
  checkAvailability,
  {
    interval: 10000, // 10秒間隔
    maxRetries: 3, // すぐ終わるはず
  },
);

const restartAutoRetry = () => {
  stopAutoRetry();
  startAutoRetry();
};

onBeforeMount(async () => {
  await startAutoRetry();
});
</script>

<style lang="scss" scoped>
.error, .processing {
  margin-top: 8px;
}
</style>
